/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  Card,
  Stack,
  FormControl,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Paper,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import { FormProvider, RHFSelect, RHFTextField } from 'components/hook-form';
import CRUD from 'utils/Crud';
import { fNumber } from 'utils/formatNumber';

const modalStyle = (isMobile, isDesktop) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: isMobile ? '98vw' : isDesktop ? 700 : 500,
  maxWidth: '98vw',
  maxHeight: isMobile ? '98vh' : '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 4,
  boxShadow: 24,
  p: isMobile ? 1 : 4,
  outline: 'none',
});

const NewUserSchema = Yup.object().shape({
  amount: Yup.string().required('Amount is required'),
  // number_of_cards: Yup.string().required('Number of cards is required'),
  password: Yup.string().required('Password is required'),
  // rate: Yup.string().required('Rate is required'),
  // vendor_rate: Yup.string().required('Vendor rate is required'),
  total_amount: Yup.string().required('Total amount is required'),
  settlement_type: Yup.string().required('Please select an option'),
});

GiftcardActionModal.propTypes = {
  handleClose: PropTypes.func,
  handleComplete: PropTypes.func,
  order: PropTypes.object.isRequired,
};

export default function GiftcardActionModal({ handleClose, handleComplete, order }) {
  // Removed useSnackbar
  const [files, setFiles] = useState([]);
  const [apiError, setApiError] = useState(""); // <-- Error state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: {
      amount: fNumber(order.amount),
      password: '',
      rate: order.rate,
      vendor_rate: order.vendor_rate,
      number_of_cards: order.number_of_cards,
      settlement_charges: 0,
      total_amount: 0,
      final_settlement_amount: fNumber(order.settlement_amount),
      silent_notification: false,
      settlement_type: ""
    },
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = methods;

  const values = watch();

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const onSubmit = async () => {
    setApiError(""); // Clear previous errors
    try {
      const amt = String(values.amount).replace(',', '');

      const formData = new FormData();
      // formData.append('vendor_rate', values.vendor_rate);
      formData.append('password', values.password);
      formData.append('silent_notification', values.silent_notification);
      formData.append('amount', amt); 
      formData.append('settlement_type', values.settlement_type);
      // formData.append('rate', values.rate);
      formData.append('order_id', order.order_id);
      // formData.append('number_of_cards', values.number_of_cards);
      // formData.append('settlement_charges', values.settlement_charges);
      formData.append('total_amount', String(values.total_amount).replace(',', ''));
      formData.append('final_settlement_amount', String(values.final_settlement_amount).replace(',', ''));

      files.forEach((file, idx) => {
        formData.append(`userfile${idx}`, file);
      });

      const ret = await axiosInstance.post(CRUD.order.giftcardOrderComplete, formData);

      reset();
      handleComplete(ret.data);
    } catch (error) {
      setApiError(getErrorMessage(error)); // <-- Set error to display in modal
    }
  };

  const handleCurrencyFormat = (e, field) => {
    const v = e.currentTarget.value.replaceAll(',', '');
    const _v = parseFloat(v).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    setValue(field, _v);
  };

  const renderFileList = () =>
    files.length > 0 && (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
        {files.map((file, idx) => (
          <Box
            key={idx}
            sx={{
              bgcolor: 'grey.100',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              mb: 0.5,
            }}
          >
            <Icon icon="mdi:file-upload-outline" width={16} height={16} style={{ marginRight: 4 }} />
            {file.name}
          </Box>
        ))}
      </Stack>
    );

  return (
    <Modal
      open
      onClose={(e, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      hideBackdrop={false}
    >
      <Box sx={modalStyle(isMobile, isDesktop)}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography id="modal-modal-title" variant="h5" component="h2" fontWeight={700}>
            Complete Gift Card Request
          </Typography>
          <IconButton onClick={handleClose} size="large" aria-label="close">
            <Icon icon="mdi:close" width={28} height={28} />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        {/* API Error Display */}
        {apiError && (
          <Paper
            elevation={0}
            sx={{
              bgcolor: 'error.lighter',
              color: 'error.dark',
              p: 2,
              mb: 2,
              border: `1px solid ${theme.palette.error.main}`,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {apiError}
            </Typography>
          </Paper>
        )}

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 1 : 3,
              bgcolor: 'background.default',
              mb: 2,
            }}
          >
            {/* Section: File Upload */}
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Upload Proof of Transaction
            </Typography>
            <Stack direction={isDesktop ? "row" : "column"} alignItems={isDesktop ? "center" : "stretch"} spacing={2} sx={{ mb: 2 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<Icon icon="mdi:upload" width={20} height={20} />}
                sx={{ textTransform: 'none', fontWeight: 500, minWidth: 160 }}
              >
                Select Images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              {renderFileList()}
            </Stack>
            <Divider sx={{ my: 2 }} />

            {/* Section: Settlement */}
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Settlement - {order.settlement_currency}{fNumber(order.settlement_amount)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Enter the total amount received after trading the gift card. This is used to calculate profit and loss for user rewards.
            </Typography>
            <RHFTextField
              name="total_amount"
              label={`Total Received Amount ${order.settlement_currency}`}
              InputProps={{
                  inputMode: 'decimal',
                  endAdornment: (
                    <InputAdornment position="end">{order.settlement_currency}</InputAdornment>
                  ),
                }}
              required
              sx={{ mb: 2, mt: 1 }}
              onFocus={(e) => e.currentTarget.select()}
              onBlur={(e) => handleCurrencyFormat(e, 'total_amount')}
            />

            <RHFTextField
              name="password"
              label="Confirm Password"
              type="password"
              required
              sx={{ mb: 2, mt: 1 }}
            />

              <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="error">
                  Note: The system must receive the settlement amount before allowing external transfer. To confirm order without automatically processing the payment kindly use Manual Settlemnt option.
                  </Typography>
              </Stack>
            
             {/* Settlement Type Select */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Settlement Type
              </Typography>
              <RHFSelect
                  name="settlement_type"
                  fullWidth
                  required
                  sx={{
                      mb: isMobile ? 2 : 3,
                      minHeight: isMobile ? 48 : 56,
                      fontSize: isMobile ? 16 : 18,
                  }}
                  >
                  <option value="" disabled>
                      -- Select Settlement Type --
                  </option>
                  <option value="offline_payment">Manual Payment (No transfer)</option>
                  <option value="paystack_transfer">Paystack transfer</option>
              </RHFSelect>

              <p style={{fontSize:12, marginBottom:5, color:"orangered"}}>Note: force the system to complete this action without notifying the user  </p>
              <FormControlLabel
                  control={<Checkbox 
                    name="silent_notification" 
                    checked={values.silent_notification}
                    onChange={(e) => setValue('silent_notification', e.target.checked)}
                    color='error' />}
                  label="Surpress Notifications"
                  sx={{ mb: 3 }}
                  />

          </Paper>

          {/* Action Buttons */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            justifyContent="flex-end"
            alignItems={isMobile ? "stretch" : "center"}
            sx={{
              position: isMobile ? 'sticky' : 'static',
              bottom: isMobile ? 0 : 'auto',
              bgcolor: isMobile ? 'background.paper' : 'inherit',
              py: isMobile ? 1 : 0,
              zIndex: 2,
              mt: 2,
            }}
          >
            <Button variant="outlined" size="large" onClick={handleClose} sx={{ minWidth: 120 }}>
              Close
            </Button>
            <LoadingButton
              type="submit"
              size="large"
              variant="contained"
              loading={isSubmitting}
              sx={{ minWidth: 160 }}
            >
              Continue
            </LoadingButton>
          </Stack>
        </FormProvider>
      </Box>
    </Modal>
  );
}
